import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";

import AdminCard from "./AdminCard";
import MemberCard from "./MemberCard";
import ActivitiesCard from "../../page/admin/ActivitiesCard";
import MembershipFeeCard from "./MembershipFeeCard";
import TransactionValueCard from "./TransactionValueCard";
import CompanyActivityPointCard from "./CompanyActivitypointCard";
import ProductTotalCard from "./ProductTotalCard";
import ProductSoldCard from "./ProductSoldCard";
import ViewCountCard from "./viewCountCard";
import ProductViewCard from "./ProductViewCard";
import MyTransactionStatisticsCard from "./MyTransactionStatisticsCard";
import MyActivityPointCard from "./MyActivityPointCard";
import MyProductFeature from "./MyProducFeature";
import AdminPostStatisticCard from "./AdminPostStatisticCard";
import AdminNotificationStatisticCard from "./AdminNotificationStatisticCard";
import AdminPartnerStatisticCard from "./AdminPartnerStatisticCard";
import AdminPostCreatedStatisticCard from "./AdminPostCreatedStatisticCard";
import AdminNotificationCreatedStatisticCard from "./AdminNotificationCreatedStatisticCard";
// import CouponStatisticCard from "./CouponStatisticCard";
// import RolesFeature from "./RolesFeature";
// import CouponApproveCard from "./CouponApproveCard";
import PostsCard from "./PostsCard";
import OrderListFeature from "./OrderListFeature";
import ContactListFeature from "./ContactListFeature";
import ProductApprovedCard from "./ProductApprovedCard";
import RolePermissionCard from "./RolePermissionCard";

import ApiCustom from "../../utils/ApiCustom";
import NewMemberInYearCard from "./NewMemberInYearCard";

/* ===== Helpers & constants ===== */

const userId = localStorage.getItem("userId");
const companyId = localStorage.getItem("companyId");

const getUserPermissions = () => {
  const permissions = localStorage.getItem("permissions");
  return permissions
    ? JSON.parse(permissions).map((p) => p.permissionSlug)
    : [];
};

const hasPermission = (requiredPermission) => {
  if (userId == 1) return true;
  const userPermissions = getUserPermissions();
  return userPermissions.includes(requiredPermission);
};

let userType = localStorage.getItem("type_user");
if (hasPermission("CONTENT_MANAGE")) {
  userType = "admin";
}

const animated = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const ReminderBanner = ({ onClose }) => {
  return (
    <div className="mb-6">
      <div className="w-full rounded-lg border border-yellow-200 bg-yellow-50 text-yellow-900 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="font-semibold">Thông báo</div>
          <button
            className="btn btn-sm btn-ghost pr-0 text-yellow-900"
            onClick={onClose}
            aria-label="Đóng thông báo"
            title="Đóng"
          >
            ✕
          </button>
        </div>

        <div className="text-sm text-justify mt-2">
          Kính chào Quý hội viên. CCA xin chân thành cảm ơn Quý hội viên đã đăng
          ký để trở thành hội viên của Hiệp Hội.
          <p>
            Nhằm phục vụ tốt các hoạt động đa dạng và cải thiện hiệu quả công
            tác của CCA trong năm, chúng tôi rất mong nhận được sự ủng hộ từ Quý
            Hội viên thông qua việc hoàn thành hội phí. Quý Hội viên có thể đóng
            hội phí bằng hình thức tiền mặt hoặc chuyển khoản:
          </p>
          <p>
            Tên tài khoản: Hội Xây Dựng Thành phố Cần Thơ
            <br />
            Số tài khoản: <strong>0111000314257</strong> tại{" "}
            <strong>Ngân hàng Vietcombank CN Cần Thơ</strong>
          </p>
          <p>
            Chúc Quý Hội viên kinh doanh thành công và ngày càng phát triển.
          </p>
        </div>
      </div>
    </div>
  );
};

const DashboardStats = () => {
  const workspace = useSelector((state) => state.workspace?.value ?? "admin");

  const effectiveWorkspace = userType === "user" ? "member" : workspace;

  const isAdminView = effectiveWorkspace === "admin" || userId == 1;
  const isMemberView = !isAdminView;

  useEffect(() => {}, [effectiveWorkspace]);

  const [needReminder, setNeedReminder] = useState(false);
  const [showReminder, setShowReminder] = useState(true);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        if (!isMemberView) {
          if (mounted) setNeedReminder(false);
          return;
        }
        const cid = (companyId || "").trim();
        if (!cid || cid === "null" || cid === "undefined") {
          if (mounted) setNeedReminder(false);
          return;
        }
        const res = await ApiCustom.get(`/membership-fee-need-reminder/${cid}`);
        const need =
          res?.data?.data?.data?.needReminder ??
          res?.data?.data?.needReminder ??
          res?.data?.needReminder ??
          false;

        if (mounted) {
          setNeedReminder(Boolean(need));
          setShowReminder(true);
        }
      } catch (e) {
        if (mounted) setNeedReminder(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [isMemberView]);

  // Card đặc biệt (admin có quyền phê duyệt)
  const specialCards = [
    // hasPermission("COUPON_APPROVE") && <CouponApproveCard key="coupon" />,
    hasPermission("PRODUCT_APPROVE") && <ProductApprovedCard key="product" />,
    hasPermission("APPROVE_COMPANY") && (
      <AdminPartnerStatisticCard key="partner" />
    ),
    hasPermission("POST_APPROVE") && (
      <AdminPostStatisticCard key="postapprove" />
    ),
    hasPermission("NOTIFICATION_APPROVE") && (
      <AdminNotificationStatisticCard key="notiappr" />
    ),
  ].filter(Boolean);

  // Card OVERVIEW (admin)
  const overviewCards = [
    <AdminCard key="admin" />,
    <MemberCard key="member" />,
    <NewMemberInYearCard key="new_member" />,
    <ActivitiesCard key="activities" />,
    <PostsCard key="posts" />,
    <MembershipFeeCard key="membershipfee" />,
    <TransactionValueCard key="transaction" />,
    <CompanyActivityPointCard key="activitypoint" />,
    <ProductTotalCard key="prodtotal" />,
    <ProductSoldCard key="prodsold" />,
    <ViewCountCard key="viewcount" />,
    <ProductViewCard key="prodview" />,
  ];

  return (
    <div className="min-h-screen py-0 md:px-0">
      <motion.div
        {...animated}
        className="flex items-center justify-between mb-2"
      />

      {/* NEW: Banner nhắc đóng hội phí ở TRÊN CÙNG (chỉ hội viên) */}
      {isMemberView && needReminder && showReminder && (
        <ReminderBanner onClose={() => setShowReminder(false)} />
      )}

      {/* ======= CHỈ HIỆN 1 NHÓM THEO WORKSPACE ======= */}
      {isAdminView ? (
        // --- GIAO DIỆN QUẢN TRỊ ---
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
          {(userType === "admin" || userId == 1) && hasPermission("ROLE") && (
            <RolePermissionCard />
          )}

          {(userType === "admin" || userId == 1) && specialCards}

          {(userType === "admin" || userId == 1) &&
            hasPermission("OVERVIEW") &&
            overviewCards}

          {userType === "admin" && !hasPermission("OVERVIEW") && (
            <>
              {hasPermission("POST_CREATE") && (
                <AdminPostCreatedStatisticCard />
              )}
              {hasPermission("POST_CREATE") && (
                <AdminNotificationCreatedStatisticCard />
              )}
              {/* {hasPermission("COUPON_CREATE") && <CouponStatisticCard />} */}
              {hasPermission("MEMBERSHIP_FEE_SHOWS") && <MembershipFeeCard />}
              {hasPermission("POINT_VIEW") && <CompanyActivityPointCard />}
              <AdminCard />
              <MemberCard />
              <NewMemberInYearCard key="new_member" />
            </>
          )}
        </div>
      ) : (
        // --- GIAO DIỆN HỘI VIÊN ---
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
            {hasPermission("BILL_SHOW") && (
              <>
                <AdminCard />
                <MemberCard />
                <MyTransactionStatisticsCard />
                <MyActivityPointCard />
              </>
            )}
          </div>

          {hasPermission("BILL_SHOW") && (
            <>
              <MyProductFeature />
              <OrderListFeature />
              <ContactListFeature />
            </>
          )}
        </>
      )}

      {/* Role feature (nếu muốn bật lại)
      {hasPermission("ROLE") && userId != 1 && <RolesFeature />} */}
    </div>
  );
};

export default DashboardStats;
